import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";

import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import { useActiveDrawerId } from "ds/components/DrawerNew/useActiveDrawerId";
import { isPathnameDifferent } from "ds/components/DrawerNew/utils";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPagePolicy } from "hooks/useAnalytics/pages/policy";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { SpacesContext } from "views/Account/SpacesProvider";
import { AccountContext } from "views/AccountWrapper";

import { showPolicyCreateDrawer } from "../PolicyCreateDrawer";
import PoliciesEmpty from "./Empty";
import PoliciesViewHeader from "./ViewHeader";
import useTakenPolicyNames from "./useTakenPolicyNames";
import PoliciesFiltersLayout from "./FiltersLayout";
import { POLICIES_TABLE_CELLS } from "./Table/Cells";
import { POLICIES_SORT_CONFIG } from "./Table/constants";
import usePoliciesTableStore from "./Table/useTableStore";
import {
  ENTITIES_PER_PAGE,
  FILTERS_ORDER_SETTINGS_KEY,
  POLICIES_LIMIT_STORAGE_KEY,
  VIRTUALIZATION_THRESHOLD,
} from "./constants";
import useSearchPolicies from "./useSearchPolicies";
import useSearchPoliciesSuggestions from "./useSearchPoliciesSuggestions";

const Policies = () => {
  const { hasEntityCreateAccess: canCreatePolicy } = useTypedContext(SpacesContext);
  const { accountName } = useTypedContext(AccountContext);
  const [urlParams] = useSearchParams();

  const activeRowId = useActiveDrawerId();
  const trackAnalytics = useAnalytics({ page: AnalyticsPagePolicy.PoliciesList });

  useTitle(`Policies · ${accountName}`);

  const {
    entities,
    error,
    isPageEmpty,
    hasNoFilteringResults,
    loadingContent,
    loadPolicies,
    startCursor,
    endCursor,
    paginationResetKey,
  } = useSearchPolicies();

  const {
    filters,
    filtersMap,
    sortOptions,
    loading: filtersLoading,
    refetchActiveSections,
    filteredCount,
  } = useSearchPoliciesSuggestions();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadPolicies,
    startCursor,
    endCursor,
    allItemsCount: filteredCount,
    visibleItemsCount: entities.length,
    initialLimit: ENTITIES_PER_PAGE,
    storageKey: POLICIES_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
    analyticsPage: AnalyticsPagePolicy.PoliciesList,
  });

  const hasRows = entities.length > 0;

  const takenPolicyNames = useTakenPolicyNames();

  const openCreateDrawer = useCallback(
    (omitQuerySearchCheck?: boolean) => {
      showPolicyCreateDrawer({
        takenPolicyNames,
        shouldCloseOnLocationChange: omitQuerySearchCheck ? isPathnameDifferent : undefined,
      });
    },
    [takenPolicyNames]
  );

  useEffect(() => {
    if (urlParams.has("create-new")) {
      openCreateDrawer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <PoliciesViewHeader />
      <PoliciesFiltersLayout
        openCreateDrawer={openCreateDrawer}
        isPageEmpty={isPageEmpty}
        filters={filters}
        filtersMap={filtersMap}
        sortOptions={sortOptions}
        filtersLoading={filtersLoading}
        refetchActiveSections={refetchActiveSections}
        canCreatePolicy={canCreatePolicy}
      >
        <TableSimple
          data={entities}
          loading={loadingContent}
          store={usePoliciesTableStore}
          ariaLabel="Policies table"
          activeRowId={activeRowId}
          analyticsPage={AnalyticsPagePolicy.PoliciesList}
          trackAnalytics={trackAnalytics}
          cellPerColumn={POLICIES_TABLE_CELLS}
          filtersStorageKey={FILTERS_ORDER_SETTINGS_KEY}
          sortConfig={POLICIES_SORT_CONFIG}
          rowsPerPage={rowsPerPage}
          virtualize={entities.length >= VIRTUALIZATION_THRESHOLD}
          emptyState={
            <PoliciesEmpty
              hasNoFilteringResults={hasNoFilteringResults}
              canCreatePolicy={canCreatePolicy}
            />
          }
        />
        {(hasRows || loadingContent) && (
          <TablePagination
            from={from}
            to={to}
            loading={loadingContent}
            goToNextPage={goToNextPage}
            goToPrevPage={goToPrevPage}
            goToFirstPage={goToFirstPage}
            goToLastPage={goToLastPage}
            setRowsPerPage={setRowsPerPage}
            rowsPerPage={rowsPerPage}
            itemsCount={filteredCount}
            lastPage={lastPage}
            page={page}
          />
        )}
      </PoliciesFiltersLayout>
    </>
  );
};

export default Policies;
